export interface Poolable {
  scene?: unknown;
  setActive(value: boolean): this;
  setVisible(value: boolean): this;
  destroy(fromScene?: boolean): void;
}

export function isPhaserDestroyed(obj: { scene?: unknown }): boolean {
  return "scene" in obj && !obj.scene;
}

/**
 * Scene-scoped reuse of display objects. Grow lazily, hide on release, destroy on scene shutdown.
 * `retire` must kill tweens/anims so onComplete cannot destroy or mutate a parked object.
 */
export class DisplayPool<T extends Poolable> {
  private readonly free: T[] = [];
  private readonly live = new Set<T>();
  private readonly created: T[] = [];
  private tornDown = false;

  constructor(
    private readonly factory: () => T,
    private readonly retire: (obj: T) => void = () => undefined,
    prewarm = 0,
  ) {
    for (let i = 0; i < prewarm; i += 1) {
      const obj = this.factory();
      this.created.push(obj);
      this.retire(obj);
      obj.setActive(false).setVisible(false);
      this.free.push(obj);
    }
  }

  get createdCount(): number {
    return this.created.length;
  }

  get liveCount(): number {
    return this.live.size;
  }

  get alive(): boolean {
    return !this.tornDown;
  }

  acquire(): T {
    if (this.tornDown) {
      throw new Error("DisplayPool.acquire after destroy");
    }
    let obj = this.free.pop();
    while (obj && isPhaserDestroyed(obj)) {
      obj = this.free.pop();
    }
    if (!obj) {
      obj = this.factory();
      this.created.push(obj);
    }
    this.live.add(obj);
    obj.setActive(true).setVisible(true);
    return obj;
  }

  release(obj: T | undefined | null): void {
    if (!obj || this.tornDown || !this.live.delete(obj)) {
      return;
    }
    if (!isPhaserDestroyed(obj)) {
      this.retire(obj);
      obj.setActive(false).setVisible(false);
    }
    this.free.push(obj);
  }

  destroy(): void {
    if (this.tornDown) {
      return;
    }
    this.tornDown = true;
    for (const obj of this.created) {
      if (isPhaserDestroyed(obj)) {
        continue;
      }
      this.retire(obj);
      obj.destroy();
    }
    this.created.length = 0;
    this.free.length = 0;
    this.live.clear();
  }
}
